<template>
  <StellaMenu v-if="currentDailyPlay" alignment="start" position="bottom">
    <template #default="{ isOpened }">
      <StellaTooltip text="Daily Play" position="bottom">
        <StellaButton
          type="ghost"
          size="sm"
          :active="isOpened"
          class="relative !sm<:px-2"
        >
          <StellaIcon
            icon="coffee"
            size="md"
            v-track:mousedown="'Daily Play Clicked'"
            v-track-props="{ component: 'Daily Play | Top Nav' }"
          />
          <div
            v-if="!currentDailyPlay.designerInfo"
            class="absolute top-0.5 right-0.5 md:top-1 md:right-1 h-2.5 w-2.5 rounded-full bg-green-30"
          />
        </StellaButton>
      </StellaTooltip>
    </template>
    <template #content="{ hide }">
      <StellaMenuPanel
        class="flex flex-col gap-1 shadow-lg text-sm rounded-sm p-4 max-w-xs bg-green-10 text-green-90"
      >
        <div class="absolute top-1 right-1 p-2 cursor-pointer" @click="hide()">
          <StellaButton icon="x" size="xs" type="text" />
        </div>
        <div class="flex gap-2 items-center mb-2">
          <StellaIcon size="md" icon="coffee" />
          <StellaText type="Label XS">New Daily Play!</StellaText>
        </div>
        <div class="flex flex-col gap-2">
          <StellaText type="Label S">
            {{ currentDailyPlay.name }}
          </StellaText>
          <StellaLink
            v-if="currentDailyPlay && currentDailyPlay.vendor"
            size="XS"
            v-track="'Vendor Clicked'"
            v-track-props="{
              component: 'Daily Play | Top Nav',
              ...vendorTrackingProps(currentDailyPlay.vendor),
            }"
            :to="{
              name: 'brand',
              params: { vendorId: currentDailyPlay.vendor.id },
            }"
          >
            {{ currentDailyPlay.vendor.name }}
          </StellaLink>
          <StellaText type="Body XS">
            {{ currentDailyPlay.prompt }}
          </StellaText>
          <StellaSpinner v-if="dailyPlaysPermission.loading.value" size="xs" />
          <StellaLink
            v-else-if="!dailyPlaysPermission.hasPermission.value"
            @click="
              showTierUpgradeModal({
                source: 'Daily Play | Top Nav',
                feature: 'DAILY_PLAYS',
              })
            "
            v-track:mousedown="'Daily Play Started'"
            v-track-props="{
              component: 'Daily Play | Top Nav',
            }"
          >
            <StellaText
              type="Label XS"
              class="text-green-90 decoration-green-90"
            >
              Join to play
            </StellaText>
          </StellaLink>
          <StellaLink
            v-else-if="thingRoute"
            :permission="dailyPlaysPermission"
            :to="thingRoute"
            trackingComponent="Daily Play | Top Nav"
            v-track:mousedown="'Daily Play Resumed'"
            v-track-props="{
              daily_play_id: currentDailyPlay.id,
              type: currentDailyPlay.type,
              component: 'Daily Play | Top Nav',
            }"
          >
            <StellaText
              type="Label XS"
              class="text-green-90 decoration-green-90"
            >
              Resume
            </StellaText>
          </StellaLink>
          <StellaLink
            v-else
            @click="
              dailyPlaysPermission.tierGuard(beginDailyPlay, {
                source: 'Daily Play | Top Nav',
              })
            "
            v-track:mousedown="'Daily Play Started'"
            v-track-props="{
              daily_play_id: currentDailyPlay.id,
              type: currentDailyPlay.type,
              component: 'Daily Play | Top Nav',
            }"
          >
            <StellaText
              type="Label XS"
              class="text-green-90 decoration-green-90"
            >
              Begin
            </StellaText>
          </StellaLink>
        </div>
      </StellaMenuPanel>
    </template>
  </StellaMenu>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import { useCurrentDailyPlayData } from '/@/common/daily-play/api'
import { useProductTracking } from '/@/common/things/tracking'
import { usePermission, useTierUpgrade } from '/@/common/tiers'
import { useTracker } from '/@/core/tracking/api'
import { stellaModal } from '/@/stella/StellaModal'
import { useProjectGuard } from '/@/common/projects/utils'
import { useToast } from '/@/core/elements/toast'
import { useNewViziModal } from '/@/common/viz/modals/NewViziModal'

const { dailyPlay } = useCurrentDailyPlayData()
const { vendorTrackingProps } = useProductTracking()
const tracker = useTracker()
const projectGuard = useProjectGuard()
const toast = useToast()
const newViziModal = useNewViziModal()

const currentDailyPlay = computed(() => dailyPlay.value?.current)

const dailyPlaysPermission = usePermission('DAILY_PLAYS')
const { showTierUpgradeModal } = useTierUpgrade()

async function beginDailyPlay() {
  if (!currentDailyPlay.value) return

  const { id, type, url } = currentDailyPlay.value

  tracker.send('Get Started Clicked', {
    component: 'Daily Play | Top Nav',
  })

  try {
    const { projectId, projectName, isNew } =
      await projectGuard.verify('Daily Play')
    const onSubmit = () => {
      if (isNew) {
        toast.show({
          type: 'stella',
          severity: 'success',
          title: 'New "Playing Around" project created!',
          message:
            'All new Daily Play and Design Challenge designs will be automatically be added to this project.',
          icon: 'check-circle',
        })
      }
    }
    if (type === 'VIZI') {
      newViziModal.show({
        dailyPlayId: id,
        project: {
          id: projectId,
          name: projectName,
        },
        onSubmit,
      })
    } else if (type === 'PALETTE') {
      stellaModal.open({
        content: defineAsyncComponent(
          () =>
            import(
              '/@/common/palettes/modals/NewPaletteModal/NewPaletteModalContent.vue'
            ),
        ),
        data: {
          dailyPlayId: id,
          project: {
            id: projectId,
            name: projectName,
          },
          onSubmit,
        },
      })
    } else {
      window.location.href = url
    }
  } catch {
    return
  }
}

const thingRoute = computed(() => {
  if (!currentDailyPlay.value?.designerInfo?.thing) return null

  switch (currentDailyPlay.value.designerInfo.thing.thingType) {
    case 'MOCKUP':
      return {
        name: 'vizi-edit',
        params: { viziId: currentDailyPlay.value.designerInfo.thing.id },
      }
    case 'PALETTE':
      return {
        name: 'palette-edit',
        params: { paletteId: currentDailyPlay.value.designerInfo.thing.id },
      }
    default:
      return null
  }
})
</script>
